import React, { useState, useEffect, useRef } from 'react';
import Grid from '@mui/material/Grid2';
import Typography from '@mui/material/Typography';

import Main from '../../../layouts/Main';
import Container from '../../../components/Container';
import { Box, Divider, TextField, InputAdornment, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import PageBreadcrumbs from '../../../components/PageBreadcrumbs';
import { useErrorBoundary } from 'react-error-boundary';
import { Video } from '../../../types/communication/video';
import SearchIcon from '@mui/icons-material/Search';
import YouTubeIcon from '@mui/icons-material/YouTube';
import { title } from 'process';
import { debounce, filter, set } from 'lodash';

const Videos = (): JSX.Element => {

  const [width, setWidth] = useState<number>(window.innerWidth);

  function handleWindowSizeChange() {
    setWidth(window.innerWidth);
  }
  useEffect(() => {
    window.addEventListener('resize', handleWindowSizeChange);
    return () => {
      window.removeEventListener('resize', handleWindowSizeChange);
    }
  }, []);

  const isMobile = width <= 768;

  const { showBoundary } = useErrorBoundary();
  const [videos, setVideos] = useState<Video[]>();
  const [filteredVideos, setFilteredVideos] = useState<Video[]>();
  const [searchText, setSearchText] = useState<string>('');

  const additionalHelp = [
    {
      title: ' What is the Depository Reporting System?',
      body:
        <>
          <Typography variant='body1' component='p'>
            The depository reporting system was established by Section 19 of M.G.L. Chapter 55, the Massachusetts campaign finance law.
          </Typography>
          <Typography variant='body1' component='p'>
            Candidates and committees in this reporting system arrange for their financial institutions to file twice-monthly reports with OCPF listing their total monthly deposits and providing detailed information about the committee's expenditures. In addition, these candidates and committees file twice-monthly reports of contribution information, and, if applicable, detailed monthly reports for any reimbursement, subvendor or credit card payments made by the committee.
          </Typography>
          <Typography variant='body1' component='p'>
            These candidates and committees are also responsible for filing year-end summary reports.
          </Typography>
        </>
    },
    {
      title: ' Who files in the Depository Reporting System?',
      body:
        <>
          <Typography variant='body1' component='p'><b>Statewide (Constitutional) Offices</b></Typography>
          <Typography sx={{ textIndent: '5' }} variant='body1' component='p'>Governor, Lieutenant Governor, Attorney General, State Secretary, State Treasurer or State Auditor.</Typography>
          <Typography variant='body1' component='p'><b>Legislative Offices</b></Typography>
          <Typography sx={{ textIndent: '5' }} variant='body1' component='p'>House and Senate.</Typography>
          <Typography variant='body1' component='p'><b>County and District Offices</b></Typography>
          <Typography sx={{ textIndent: '5' }} variant='body1' component='p'>County Clerks of Court, Commissioners and Treasurers; Registers of Deeds and Probate; District Attorneys; Sheriffs and Suffolk County Clerks of the Superior and Supreme Judicial Courts.</Typography>
          <Typography variant='body1' component='p'><b>Governor's Council</b></Typography>
          <Typography sx={{ textIndent: '5' }} variant='body1' component='p'>One of the eight district seats of the Executive Council.</Typography>
          <Typography variant='body1' component='p'><b>Mayors</b></Typography>
          <Typography sx={{ textIndent: '5' }} variant='body1' component='p'>All mayoral candidates file in the depository system.</Typography>
          <Typography variant='body1' component='p'><b>City Councilors in Cities with</b></Typography>
          <Typography variant='body1' component='p'><b>65,000+ Populations</b></Typography>
          <Typography sx={{ textIndent: '5' }} variant='body1' component='p'>This includes the communities of Boston, Brockton, Cambridge, Fall River, Framingham, Lawrence, Lowell, Lynn, New Bedford, Newton, Quincy, Somerville, Springfield and Worcester.</Typography>
          <Typography variant='body1' component='p'><b>State Party Committees</b></Typography>
          <Typography sx={{ textIndent: '5' }} variant='body1' component='p'>The state committees of political parties organized under M.G.L. chapter 52, section 1.</Typography>
          <Typography variant='body1' component='p'><b>State Political Action Committees (PACs)</b></Typography>
          <Typography sx={{ textIndent: '5' }} variant='body1' component='p'>PACs and People's Committees.</Typography>
        </>
    },
    {
      title: 'Who are the "Non-depository" Candidates and Committees?',
      body:
        <>
          <Typography variant='body1' component='p'>Non-depository candidates and committees are candidates the Barnstable Assembly of Delegates, local party committees and ballot question committees</Typography>
          <Typography variant='body1' component='p'>These candidates and committees file directly with OCPF.</Typography>
          <Typography variant='body1' component='p'>Local party committees file an annual year-end report, with two extra reports due in years in which they support or oppose candidates on the ballot: a pre-primary report and a pre-election report.</Typography>
          <Typography variant='body1' component='p'>Ballot question committees file on a slightly different schedule before and after the election.</Typography>
          <Typography variant='body1' component='p'>With the exception of the depository candidates noted above, municipal candidates and committees, including local ballot question committees, file locally based on the dates of their local elections. Those reports are available from local city or town clerks or election commissions.</Typography>
        </>
    }


  ]

  const videoSections = [
    {
      columns: 1,
      sections: [
        { title: 'Candidates who file with OCPF', groupId: 1 },
        { title: 'Municipal Candidates and Committees who File Locally', groupId: 3 },
        { title: 'Q&A', groupId: 4 },
        { title: 'Additional Help & Information', groupId: 9 },
      ]
    },
    {
      columns: 2,
      sections: [
        { title: 'Political Action Committees', groupId: 5 },
        { title: 'Local Parties and State Parties', groupId: 2 },
        { title: 'OCPF Legal Videos', groupId: 6 },
        { title: 'OCPF Audit Videos', groupId: 7 },
        { title: 'IEPACs and Independent Spending', groupId: 8 }
      ]
    }
  ];


  const filterVideo = debounce((event: any) => {
    const value = event.target.value;
    setSearchText(value);
  }, 800);

  const getVideos = async () => {

    try {
      await fetch(`${process.env.REACT_APP_API_BASE_URL}/videos/`)
        .then(result => {
          if (!result.ok) {
            showBoundary(new Error('Failed to get videos.'));
          }
          return result.json();
        })
        .then(result => {
          setVideos(result);
          setFilteredVideos(result);
        });
    } catch (error) {
      console.error('Uncaught Error:', error);
    }
  }

  //had to add videoaccordion component to conditionally render because of the iframe
  const VideoAccordion = (video: Video): JSX.Element => {
    const [expanded, setExpanded] = useState(false);

    const handleExpanded = () => {
      setExpanded(!expanded);
    }

    return (
      <Accordion key={video.id} onClick={() => handleExpanded()}>
        <AccordionSummary expandIcon={<ArrowDropDownIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'flex-end' }}>
            <YouTubeIcon sx={{ mr: 1, my: 0.5 }} />
            <Typography variant='subtitle1'>{video.title}</Typography>
          </Box>

        </AccordionSummary>
        <AccordionDetails>
          {expanded ? (
            <iframe
              allowFullScreen={true}
              height={'315'}
              width={'100%'}
              title={video.title}
              src={video.embedUrl}
            />
          ) : <iframe
            allowFullScreen={true}
            height={'315'}
            width={'100%'}
            title={video.title}
          />}
        </AccordionDetails>
      </Accordion>
    )

  }

  const renderVideoGroup = (title: string, groupId: number): JSX.Element => {
    return (
      <Box margin={2}>
        <Accordion defaultExpanded>
          <AccordionSummary>
            <Typography variant='h6'>{title}</Typography>
          </AccordionSummary>
          <AccordionDetails>

            {groupId === 9 ?

              additionalHelp.map(accordion => {
                return (
                  <Accordion key={accordion.title}>
                    <AccordionSummary expandIcon={<ArrowDropDownIcon />}>
                      <Typography variant='subtitle1'>{accordion.title}</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      {accordion.body}
                    </AccordionDetails>
                  </Accordion>
                )
              }
              )

              :

              //had to add videoaccordion component to conditionally render because of the iframe
              filteredVideos?.filter(video => video.groupId === groupId).sort((a, b) => a.order - b.order).map(video => {
                return <VideoAccordion key={video.id} {...video} />
              })
            }
          </AccordionDetails>
        </Accordion>
      </Box>
    )
  }

  const handleFilterChange = () => {
    const filtered = videos?.filter(video => video.title.toLowerCase().includes(searchText.toLowerCase()));
    setFilteredVideos(filtered);
  }

  useEffect(() => {
    getVideos();
  }, []);

  useEffect(() => {
    handleFilterChange();
  }, [searchText]);

  return (
    <Main>
      <Container>
        <PageBreadcrumbs></PageBreadcrumbs>
        <Typography
          variant="h5"
          align={'left'}
          data-aos={'fade-up'}
          sx={{
            fontWeight: 700,
          }}
          paddingTop={3}
          gutterBottom
          marginBottom={2}
        >
          Help Videos
        </Typography>
        <Divider />
        <Grid container marginTop={3}>
          <Grid size={12}>
            <TextField
              id="input-with-icon-textfield"
              label="Search"
              onChange={filterVideo}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                },
              }}
              variant="standard"
            />
          </Grid>

          <Grid size={isMobile ? 12 : 6}>
            {isMobile ?
              videoSections.map((section, index) => {
                return (
                  <Grid container key={section.columns + index}>
                    {section.sections.map(section => {
                      return (
                        <Grid size={12} key={section.title}>
                          {renderVideoGroup(section.title, section.groupId)}
                        </Grid>
                      )
                    })}
                  </Grid>
                )
              }
              )

              :

              videoSections.filter(section => section.columns === 1).map((section, index) => {
                return (
                  <Grid container key={section.columns + index}>
                    {section.sections.map(section => {
                      return (
                        <Grid size={12} key={section.title}>
                          {renderVideoGroup(section.title, section.groupId)}
                        </Grid>
                      )
                    })}
                  </Grid>
                )
              }
              )}
          </Grid>
          {!isMobile && <Grid size={6}>
            {videoSections.filter(section => section.columns === 2).map((section, index) => {
              return (
                <Grid container key={section.columns + index}>
                  {section.sections.map(section => {
                    return (
                      <Grid size={12} key={section.title}>
                        {renderVideoGroup(section.title, section.groupId)}
                      </Grid>
                    )
                  })}
                </Grid>
              )
            }
            )}
          </Grid>}

        </Grid>
      </Container>
    </Main>
  );
};

export default Videos;